import { useState, useEffect, useCallback, useRef, type CSSProperties } from 'react';
import { apiService, type SchematicDTO } from '@/services/api.service';

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  identifier: string;
}

const cardStyle: CSSProperties = {
  marginTop: 20,
  background: 'var(--color-surface, #16181d)',
  border: '1px solid var(--color-border, rgba(255,255,255,0.08))',
  borderRadius: 12,
  padding: 16,
};

/**
 * Section « Schematics » du détail serveur (Supervision) : liste / télécharge / envoie les .schematic
 * du dossier mods-data/schematics d'un serveur.
 *
 * Toujours visible sur chaque serveur : si le dossier n'existe pas encore (404 côté satellite), on
 * affiche l'état vide + le bouton d'envoi (le premier upload crée le dossier). Tout est délégué au
 * satellite du projet via /api/console/servers/{id}/schematics (le panel signe l'assertion, le
 * satellite parle à Pterodactyl). Scope forcé côté satellite à /mods-data/schematics.
 */
export function ServerSchematics({ identifier }: Props) {
  const [files, setFiles] = useState<SchematicDTO[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await apiService.listSchematics(identifier);
    // Toujours visible : si le dossier n'existe pas encore (404 -> pas de data), on affiche quand
    // meme la section avec une liste vide ; le premier upload creera le dossier mods-data/schematics.
    setFiles(res.data?.files ?? []);
    setLoading(false);
  }, [identifier]);

  useEffect(() => { void load(); }, [load]);

  const download = async (name: string) => {
    setBusy(name);
    setError(null);
    try {
      const blob = await apiService.downloadSchematic(identifier, name);
      if (!blob) { setError(`Telechargement de ${name} echoue`); return; }
      // L'en-tete Authorization interdit un simple <a href>, on passe donc par un blob.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(null);
    }
  };

  const onPick = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setError(null);
    for (const f of Array.from(list)) {
      if (!f.name.toLowerCase().endsWith('.schematic')) {
        setError(`${f.name} ignore (.schematic requis)`);
        continue;
      }
      setBusy(f.name);
      const r = await apiService.uploadSchematic(identifier, f.name, f);
      if (!r.ok) setError(`Envoi de ${f.name} echoue : ${r.error ?? ''}`);
    }
    setBusy(null);
    if (inputRef.current) inputRef.current.value = '';
    await load();
  };

  const btnBase: CSSProperties = {
    borderRadius: 6,
    cursor: busy ? 'not-allowed' : 'pointer',
    border: '1px solid var(--color-border, rgba(255,255,255,0.15))',
  };

  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 16 }}>🧱</span>
        <h3 style={{ margin: 0, fontSize: 15, color: 'var(--color-text, #e9e9ec)' }}>Schematics</h3>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy !== null}
          style={{ ...btnBase, padding: '6px 12px', fontSize: 13, background: 'var(--color-primary, #ff5c00)', color: '#fff' }}
        >
          {busy ? 'Envoi…' : '+ Envoyer un schematic'}
        </button>
        <input ref={inputRef} type="file" accept=".schematic" multiple hidden
          onChange={(e) => void onPick(e.target.files)} />
      </div>

      {error && (
        <div style={{
          marginBottom: 10, padding: '8px 10px', borderRadius: 6, fontSize: 13,
          background: 'var(--color-danger-bg, rgba(220,50,50,0.12))',
          color: 'var(--color-danger, #ff6b6b)',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--color-text-muted, #9aa0aa)' }}>Chargement…</div>
      ) : (files && files.length > 0) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {files.map((f) => (
            <div key={f.name} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6,
              background: 'var(--color-background-secondary, rgba(255,255,255,0.03))',
              border: '1px solid var(--color-border, rgba(255,255,255,0.06))',
            }}>
              <span style={{ flex: 1, fontFamily: 'var(--font-family-mono, monospace)', fontSize: 13, color: 'var(--color-text, #e9e9ec)', wordBreak: 'break-all' }}>{f.name}</span>
              <span style={{ fontSize: 12, color: 'var(--color-text-muted, #9aa0aa)' }}>{fmtSize(f.size)}</span>
              <button
                type="button"
                onClick={() => void download(f.name)}
                disabled={busy !== null}
                style={{ ...btnBase, padding: '4px 10px', fontSize: 12, background: 'var(--color-surface, transparent)', color: 'var(--color-text, #e9e9ec)' }}
              >
                {busy === f.name ? '…' : 'Telecharger'}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--color-text-muted, #9aa0aa)' }}>
          Aucun schematic. Utilise « + Envoyer un schematic » pour en ajouter.
        </div>
      )}
    </section>
  );
}
